import { useQuery } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";

import { TCompressPdfResDto } from "@/modules/irohana-components/api/services/global/file/dtos/compress-pdf.dto";
import { TGetFileInfoResDto } from "@/modules/irohana-components/api/services/global/file/dtos/get-file-info.dto";
import { TGetSingedUrlResDto } from "@/modules/irohana-components/api/services/global/file/dtos/get-signed-url";
import {
  TAvatarVariant,
  TUploadAvatarAllVariantsResDto,
} from "@/modules/irohana-components/api/services/global/file/dtos/upload-avatar-all-variants.dto";
import { TUploadFileResDto } from "@/modules/irohana-components/api/services/global/file/dtos/upload-file.dto";
import { TFileUploadFor } from "@/modules/irohana-components/constants/file";
import { QUERY_KEYS } from "@/modules/irohana-components/constants/queryKeys";
import { createUrlWithParams } from "@/modules/irohana-components/utils/url";
import { globalApi } from "@/modules/irohana-components/utils/api";

export const uploadEmployeeExternalFileApi = async (payload: {
  uploadFor: TFileUploadFor;
  file: File;
  token?: string;
  employeeId?: string;
}): Promise<TUploadFileResDto> => {
  const formData = new FormData();
  formData.append("file", payload.file);
  const url = createUrlWithParams("/files/upload-file", { uploadFor: payload.uploadFor, token: payload.token, employeeId: payload.employeeId });
  const rs = await globalApi.post(url, formData);
  return rs.data;
};

export const uploadFileApi = async (
  payload: { uploadFor: TFileUploadFor; file: File; token?: string },
  onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<TUploadFileResDto> => {
  // Skip real API call in Storybook
  if (window.__STORYBOOK_PREVIEW__) {
    onUploadProgress?.({ loaded: 100, total: 100 } as AxiosProgressEvent);
    return { data: { filePath: `mock/${payload.file.name}` } } as TUploadFileResDto;
  }

  const formData = new FormData();
  formData.append("file", payload.file);
  const url = createUrlWithParams("/files/upload-file", { uploadFor: payload.uploadFor, token: payload.token });
  const rs = await globalApi.post(url, formData, {
    onUploadProgress,
  });
  return rs.data;
};

/**
 * Upload avatar with selected variants in ONE API call (optimized)
 * - Detects face ONCE using Google Vision API
 * - Returns only requested variants (or all if not specified)
 * @param payload.file - Image file to upload
 * @param payload.token - Optional auth token for external employee upload
 * @param payload.employeeId - Optional employee ID for external auth
 * @param payload.variants - Optional variants to generate (default: all)
 */
export const uploadAvatarAllVariantsApi = async (
  payload: { file: File; token?: string; employeeId?: string; variants?: TAvatarVariant[] },
  onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<TUploadAvatarAllVariantsResDto> => {
  const formData = new FormData();
  formData.append("file", payload.file);
  const url = createUrlWithParams("/files/upload-avatar-all-variants", {
    token: payload.token,
    employeeId: payload.employeeId,
    variants: payload.variants,
  });
  const rs = await globalApi.post(url, formData, {
    onUploadProgress,
  });
  return rs.data;
};

export const fetchFileInfoApi = async (filePath: string): Promise<TGetFileInfoResDto> => {
  // Skip real API call in Storybook
  if (window.__STORYBOOK_PREVIEW__) {
    const name = filePath.split("/").pop() || "mock-file";
    return { data: { filePath, name, size: 1000000, mimeType: "application/pdf", updatedAt: new Date().toISOString() } } as TGetFileInfoResDto;
  }

  const url = createUrlWithParams("/files", { filePath });
  const rs = await globalApi.get(url);
  return rs.data;
};

export const useFileInfo = (filePath?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.FILE.FETCH_INFO, filePath],
    queryFn: () => (filePath ? fetchFileInfoApi(filePath) : undefined),
    enabled: Boolean(filePath),
  });
};

export const fetchSignedUrlApi = async (filePath: string, size?: string): Promise<TGetSingedUrlResDto> => {
  const url = createUrlWithParams("/files/signed-url", { filePath, size });
  const rs = await globalApi.get(url);
  return rs.data;
};

export const useFileSignedUrl = (filePath?: string | null, size?: "300x400" | "500x600") => {
  return useQuery({
    queryKey: [QUERY_KEYS.FILE.FETCH_SIGNED_URL, filePath, size],
    queryFn: () => (filePath ? fetchSignedUrlApi(filePath, size) : undefined),
    enabled: Boolean(filePath),
  });
};

export const compressPdfApi = async (
  payload: { targetSizeMb: number; file: File },
  onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<TCompressPdfResDto> => {
  const formData = new FormData();
  formData.append("file", payload.file);
  const url = createUrlWithParams("/files/compress-pdf", { targetSizeMb: payload.targetSizeMb });
  const rs = await globalApi.post(url, formData, {
    onUploadProgress,
  });
  return rs.data;
};
